import { Badge } from "@groot/ui/badge";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Button } from "@groot/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/app/shared/ui/sheet";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Switch } from "@/app/shared/ui/switch";
import { useConfirm } from "@groot/ui/primitives";
import {
  useCreateWorkflow,
  useDeleteWorkflow,
  useDuplicateWorkflow,
  useExecuteWorkflow,
  useUpdateWorkflow,
  useWorkflows,
} from "@/pages/workflows/hooks/useWorkflows";
import type { CreateWorkflowDTO, Workflow, WorkflowTriggerConfig } from "@/types";
import { Copy, Edit, Filter, Play, Plus, Trash2, X, Zap } from "lucide-react";
import { useState } from "react";
import { FlowBuilder } from "./components/FlowBuilder";
import type { WorkflowFormData } from "./components/constants";

export default function Workflows() {
  const { data: workflows, isLoading } = useWorkflows();
  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();
  const deleteWorkflow = useDeleteWorkflow();
  const duplicateWorkflow = useDuplicateWorkflow();
  const executeWorkflow = useExecuteWorkflow();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [formData, setFormData] = useState<WorkflowFormData>({
    name: "",
    description: "",
    enabled: true,
    triggerType: "email_received",
    triggerAccountId: "",
    cronExpression: "",
    emailFrom: "",
    emailSubjectContains: "",
    emailBodyContains: "",
    attachmentIgnoreList: "",
    actions: [{ type: "unlock_pdf" }],
  });

  const confirm = useConfirm();

  const handleOpenDialog = (workflow?: Workflow) => {
    if (workflow) {
      setEditingWorkflow(workflow);
      const config = workflow.trigger.config;
      setFormData({
        name: workflow.name,
        description: workflow.description || "",
        enabled: workflow.enabled,
        triggerType: workflow.trigger.type,
        triggerAccountId: config.accountId?.toString() || "",
        cronExpression: config.cronExpression || "",
        emailFrom: config.emailFrom || "",
        emailSubjectContains: config.emailSubjectContains || "",
        emailBodyContains: config.emailBodyContains || "",
        attachmentIgnoreList: (config.attachmentIgnoreList || []).join("\n"),
        actions: workflow.actions.map((a) => {
          const config = a.config as Record<string, unknown>;
          return {
            type: a.type,
            password: (config.password as string) || "",
            accountId: (config.accountId as number | undefined)?.toString() || "",
            url: (config.url as string) || "",
            // add_as_statement fields
            statementType: (config.statementType as string) || "extraction",
            triggerExtraction: (config.triggerExtraction as boolean) || false,
            autoUpload: (config.autoUpload as boolean) || false,
            renameFile: (config.renameFile as boolean) || false,
            ignorePagesFromEnd: (config.ignorePagesFromEnd as number) ?? undefined,
            aiModel: (config.aiModel as string) || undefined,
          };
        }),
      });
    } else {
      setEditingWorkflow(null);
      setFormData({
        name: "",
        description: "",
        enabled: true,
        triggerType: "email_received",
        triggerAccountId: "",
        cronExpression: "",
        emailFrom: "",
        emailSubjectContains: "",
        emailBodyContains: "",
        attachmentIgnoreList: "",
        actions: [{ type: "unlock_pdf" }],
      });
    }
    setIsSheetOpen(true);
  };

  const handleSubmit = async () => {
    // Build trigger config based on trigger type
    const triggerConfig: WorkflowTriggerConfig = {};

    if (formData.triggerAccountId) {
      triggerConfig.accountId = Number(formData.triggerAccountId);
    }

    if (formData.triggerType === "scheduled") {
      if (formData.cronExpression) triggerConfig.cronExpression = formData.cronExpression;
    }

    if (formData.triggerType === "email_received") {
      if (formData.emailFrom) triggerConfig.emailFrom = formData.emailFrom;
      if (formData.emailSubjectContains)
        triggerConfig.emailSubjectContains = formData.emailSubjectContains;
      if (formData.emailBodyContains) triggerConfig.emailBodyContains = formData.emailBodyContains;
      if (formData.attachmentIgnoreList.trim()) {
        triggerConfig.attachmentIgnoreList = formData.attachmentIgnoreList
          .split(/\n/)
          .map((p) => p.trim())
          .filter(Boolean);
      }
    }

    const workflowData: CreateWorkflowDTO = {
      name: formData.name,
      description: formData.description,
      enabled: formData.enabled,
      trigger: {
        type: formData.triggerType,
        config: triggerConfig,
      },
      actions: formData.actions.map((action) => ({
        type: action.type,
        config:
          action.type === "unlock_pdf"
            ? { password: action.password || "" }
            : action.type === "webhook"
              ? { url: action.url || "", method: "POST" }
              : action.type === "add_as_statement"
                ? {
                    accountId: Number(action.accountId) || undefined,
                    statementType: action.statementType || "extraction",
                    triggerExtraction: action.triggerExtraction || false,
                    autoUpload: action.autoUpload || false,
                    renameFile: action.renameFile || false,
                    ignorePagesFromEnd: action.ignorePagesFromEnd ?? undefined,
                    aiModel: action.aiModel || undefined,
                  }
                : {},
      })),
    };

    if (editingWorkflow) {
      await updateWorkflow.mutateAsync({
        id: editingWorkflow.id,
        data: workflowData,
      });
    } else {
      await createWorkflow.mutateAsync(workflowData);
    }
    setIsSheetOpen(false);
  };

  const handleDelete = async (id: number) => {
    if (
      !(await confirm({
        title: "Confirm Action",
        description: "Are you sure you want to delete this workflow?",
      }))
    ) {
      return;
    }
    await deleteWorkflow.mutateAsync(id);
  };

  const handleExecute = async (id: number) => {
    await executeWorkflow.mutateAsync({ id, context: {} });
  };

  const handleDuplicate = async (id: number) => {
    await duplicateWorkflow.mutateAsync(id);
  };

  const getFiltersSummary = (config: WorkflowTriggerConfig) => {
    const filters: string[] = [];
    if (config.emailFrom) filters.push(`from: ${config.emailFrom}`);
    if (config.emailSubjectContains) filters.push(`subject: *${config.emailSubjectContains}*`);
    return filters.join(", ");
  };

  if (isLoading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Workflows"
        description="Automate your finance tasks with custom workflows"
        actions={
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="size-4 mr-2" />
            New Workflow
          </Button>
        }
        className="mb-8"
      />

      {!workflows || workflows.length === 0 ? (
        <div className="text-center py-20">
          <Zap className="w-12 h-12 text-muted-foreground/70 mx-auto mb-4" />
          <h3 className="text-base font-medium text-foreground mb-2">No workflows yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create your first workflow to automate your finance tasks
          </p>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="size-4 mr-2" />
            Create Workflow
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {workflows.map((workflow) => (
            <div key={workflow.id} className="border border-border rounded-lg overflow-hidden">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-medium text-foreground">{workflow.name}</h3>
                    <Badge variant={workflow.enabled ? "default" : "secondary"}>
                      {workflow.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExecute(workflow.id)}
                      disabled={!workflow.enabled}
                    >
                      <Play className="size-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(workflow)}>
                      <Edit className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicate(workflow.id)}
                      title="Duplicate workflow"
                    >
                      <Copy className="size-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(workflow.id)}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {workflow.description && (
                  <p className="text-sm text-muted-foreground mb-3">{workflow.description}</p>
                )}
                <div className="flex items-start gap-6 text-sm">
                  <div>
                    <span className="text-muted-foreground">Trigger:</span>{" "}
                    <Badge variant="outline">{workflow.trigger.type.replace(/_/g, " ")}</Badge>
                    {Object.keys(workflow.trigger.config).length > 0 && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <Filter className="size-3" />
                        <span>{getFiltersSummary(workflow.trigger.config)}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Actions:</span>{" "}
                    {workflow.actions.map((action, idx) => (
                      <Badge key={`${action.type}-${idx}`} variant="outline" className="ml-1">
                        {action.type.replace(/_/g, " ")}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-6xl h-full flex flex-col p-0 gap-0 overflow-hidden"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="p-6 pb-2 border-b border-border bg-background z-10">
            <SheetHeader>
              <SheetTitle>{editingWorkflow ? "Edit Workflow" : "Create Workflow"}</SheetTitle>
              <Button
                size="icon"
                variant="ghost"
                className="absolute top-4 right-4 size-7"
                onClick={() => setIsSheetOpen(false)}
              >
                <X className="size-4" />
                <span className="sr-only">Close</span>
              </Button>
              <SheetDescription>Automate your finance tasks with workflows</SheetDescription>
            </SheetHeader>

            <div className="flex gap-4 mt-4 items-start">
              <div className="flex-1 space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Process HDFC Credit Card Statement"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Short description"
                />
              </div>
              <div className="flex items-center gap-2 pt-8">
                <Label htmlFor="enabled">Enabled</Label>
                <Switch
                  id="enabled"
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                />
              </div>
            </div>
          </div>

          <div className="flex-1 bg-muted overflow-hidden relative">
            <FlowBuilder formData={formData} setFormData={setFormData} />
          </div>

          <div className="p-4 border-t border-border flex justify-end gap-2 bg-background z-10">
            <Button variant="outline" onClick={() => setIsSheetOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.name || formData.actions.length === 0}
            >
              {editingWorkflow ? "Update" : "Create"} Workflow
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </Page>
  );
}
